import { useEffect } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { Stack } from "@mui/material";
import * as Sentry from "@sentry/react";

import NotFound from "@/common/components/NotFound/NotFound";
import {
  CHUNK_LOAD_FINGERPRINT,
  isChunkLoadError,
  RouteChunkLoadError,
  RouteRenderError,
} from "@/common/routing/chunkLoadError";
import { SomethingWentWrong } from "@/ui/components/SomethingWentWrong/SomethingWentWrong";

type PageErrorProps = {
  error: unknown;
  /** Admin says exactly what failed (AD1); layout keeps the generic wording. */
  verbose?: boolean;
};

const detailOf = (error: unknown) =>
  error instanceof Error
    ? `${error.name}: ${error.message}`
    : `Unexpected error: ${String(error)}`;

const withDetail = (message: string, error: unknown, verbose: boolean) =>
  verbose ? `${message} (${detailOf(error)})` : message;

/**
 * What a page shows when it could not render: a reload prompt when its code could not be
 * downloaded (usually a deploy replaced the chunk the open tab names), the generic error
 * otherwise. Reports nothing; the caller (route or error boundary) does.
 */
export const PageError = ({ error, verbose = false }: PageErrorProps) =>
  isChunkLoadError(error) ? (
    <Stack height="100%" data-testid="pageChunkLoadError">
      <SomethingWentWrong
        title="This page could not be downloaded"
        message={withDetail(
          "The app may have been updated since this tab opened, or the network dropped. Reload to get the latest version.",
          error,
          verbose
        )}
        buttonLabel="Reload"
      />
    </Stack>
  ) : (
    <Stack height="100%" data-testid="pageRenderError">
      <SomethingWentWrong
        title={verbose ? "This page failed to load" : undefined}
        message={
          verbose
            ? withDetail("Reload the page to try again.", error, verbose)
            : undefined
        }
      />
    </Stack>
  );

type RouteErrorPageProps = {
  verbose?: boolean;
  /** Where the not-found page sits in the app's frame. */
  notFoundSx?: Readonly<Record<string, string | number>>;
};

/**
 * A route's `errorElement`, in both apps: not found for a 404, a reload prompt for a chunk that
 * could not be downloaded (reported once as a warning), the page error otherwise (reported).
 */
export const RouteErrorPage = ({
  verbose = false,
  notFoundSx,
}: RouteErrorPageProps) => {
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;

  useEffect(() => {
    if (notFound) return;
    if (isChunkLoadError(error)) {
      Sentry.captureException(new RouteChunkLoadError(undefined, error), {
        level: "warning",
        fingerprint: CHUNK_LOAD_FINGERPRINT,
      });
    } else {
      Sentry.captureException(new RouteRenderError(undefined, error));
    }
  }, [error, notFound]);

  return notFound ? (
    <NotFound sx={notFoundSx} />
  ) : (
    <PageError error={error} verbose={verbose} />
  );
};
